'use client';

/**
 * SpotlightTour Component
 *
 * A multi-step guided tour that highlights UI elements with a spotlight effect.
 * Perfect for onboarding new users or introducing new features.
 * Supports step indicators, keyboard navigation, and persistence.
 */

import PrevIcon from '@mui/icons-material/ArrowBack';
import NextIcon from '@mui/icons-material/ArrowForward';
import CompleteIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { alpha, Box, Button, IconButton, LinearProgress, Portal, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { glassEffects } from '../../theme/shadows';
import { KeyboardHint } from '../shared/GuidanceStyles';

// ============================================================================
// Types
// ============================================================================

export type SpotlightPlacement = 'top' | 'bottom' | 'left' | 'right' | 'auto';

export interface TourStep {
  /** Unique step identifier */
  id: string;
  /** CSS selector for the target element */
  target: string;
  /** Step title */
  title: string;
  /** Step description/content */
  content: React.ReactNode;
  /** Tooltip placement relative to target */
  placement?: SpotlightPlacement;
  /** Highlight padding around target */
  highlightPadding?: number;
  /** Custom action button text */
  actionText?: string;
  /** Callback when action is clicked */
  onAction?: () => void;
  /** Whether to disable next until action is performed */
  requireAction?: boolean;
  /** Route to navigate to before highlighting (for multi-page tours) */
  route?: string;
  /** Callback when step becomes active */
  onEnter?: () => void;
  /** Callback when step is exited */
  onExit?: () => void;
}

export interface SpotlightTourProps {
  /** Whether the tour is active */
  active: boolean;
  /** Tour steps */
  steps: TourStep[];
  /** Current step index (controlled) */
  currentStep?: number;
  /** Callback when step changes */
  onStepChange?: (step: number) => void;
  /** Callback when tour completes */
  onComplete?: () => void;
  /** Callback when tour is skipped/closed */
  onClose?: () => void;
  /** Show step progress */
  showProgress?: boolean;
  /** Show step counter */
  showStepCounter?: boolean;
  /** Allow skipping the tour */
  allowSkip?: boolean;
  /** Skip button text */
  skipText?: string;
  /** Next button text */
  nextText?: string;
  /** Previous button text */
  prevText?: string;
  /** Finish button text */
  finishText?: string;
  /** Enable keyboard navigation */
  enableKeyboardNav?: boolean;
  /** Scroll to element when step changes */
  scrollToElement?: boolean;
  /** Scroll behavior */
  scrollBehavior?: ScrollBehavior;
  /** Storage key for persistence */
  storageKey?: string;
  /** Spotlight border radius */
  spotlightBorderRadius?: number;
  /** Callback to navigate to a route (for multi-page tours) */
  onNavigate?: (route: string) => void;
}

// ============================================================================
// Styled Components
// ============================================================================

const SpotlightOverlayContainer = styled(motion.div)({
  position: 'fixed',
  inset: 0,
  zIndex: 1300,
  pointerEvents: 'none',
  overflow: 'hidden',
});

const SpotlightCutout = styled(motion.div)(({ theme }) => {
  const isDark = theme.palette.mode === 'dark';
  const primary = theme.palette.primary.main;
  const sheen = isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.55)';
  const edge = isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.45)';
  return {
    position: 'fixed',
    zIndex: 1300,
    pointerEvents: 'none',
    background: 'transparent',
    border: `1px solid ${edge}`,
    boxShadow: [
      `inset 0 1px 0 ${sheen}`,
      `inset 0 -1px 0 ${alpha(primary, 0.18)}`,
      `0 0 0 1px ${alpha(primary, 0.35)}`,
      `0 0 24px 4px ${alpha(primary, 0.28)}`,
      `0 0 60px 12px ${alpha(primary, 0.18)}`,
      `0 0 0 9999px rgba(0, 0, 0, 0.18)`,
    ].join(', '),
    transition: 'all 300ms ease',
  };
});

interface TooltipContainerProps {
  $isDark: boolean;
}

const TooltipContainer = styled(motion.div, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<TooltipContainerProps>(({ theme, $isDark }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    position: 'fixed',
    zIndex: 1301,
    width: 340,
    padding: theme.spacing(2.5),
    borderRadius: 16,
    background: $isDark ? 'rgba(13, 33, 55, 0.98)' : 'rgba(255, 255, 255, 0.98)',
    backdropFilter: glass.backdropBlur,
    border: `1px solid ${glass.border}`,
    boxShadow: `0 20px 40px -8px rgba(0, 0, 0, 0.3), 0 0 0 1px ${alpha(theme.palette.primary.main, 0.1)}`,
  };
});

const StepIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(2),
}));

const StepDot = styled(Box, {
  shouldForwardProp: (prop) => !String(prop).startsWith('$'),
})<{ $active: boolean; $completed: boolean }>(({ theme, $active, $completed }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: $completed
    ? theme.palette.success.main
    : $active
      ? theme.palette.primary.main
      : alpha(theme.palette.text.primary, 0.2),
  transition: 'all 200ms ease',
}));

const ActionsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: theme.spacing(2.5),
  paddingTop: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

// ============================================================================
// Utility Functions
// ============================================================================

function getElementRect(selector: string): DOMRect | null {
  const element = document.querySelector(selector);
  return element?.getBoundingClientRect() ?? null;
}

function calculateTooltipPosition(
  targetRect: DOMRect,
  placement: SpotlightPlacement,
  tooltipWidth: number,
  tooltipHeight: number,
): { top: number; left: number; actualPlacement: SpotlightPlacement } {
  const padding = 16;
  const gap = 12;
  const viewport = {
    width: window.innerWidth,
    height: window.innerHeight,
  };

  let actualPlacement = placement;

  // Auto-detect best placement
  if (placement === 'auto') {
    const spaceTop = targetRect.top;
    const spaceBottom = viewport.height - targetRect.bottom;
    const spaceLeft = targetRect.left;
    const spaceRight = viewport.width - targetRect.right;

    const spaces = [
      { placement: 'bottom' as SpotlightPlacement, space: spaceBottom },
      { placement: 'top' as SpotlightPlacement, space: spaceTop },
      { placement: 'right' as SpotlightPlacement, space: spaceRight },
      { placement: 'left' as SpotlightPlacement, space: spaceLeft },
    ];

    actualPlacement = spaces.reduce((a, b) => (a.space > b.space ? a : b)).placement;
  }

  let top = 0;
  let left = 0;

  switch (actualPlacement) {
    case 'top':
      top = targetRect.top - tooltipHeight - gap;
      left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
      break;
    case 'bottom':
      top = targetRect.bottom + gap;
      left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
      break;
    case 'left':
      top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
      left = targetRect.left - tooltipWidth - gap;
      break;
    case 'right':
      top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
      left = targetRect.right + gap;
      break;
  }

  // Clamp to viewport
  left = Math.max(padding, Math.min(left, viewport.width - tooltipWidth - padding));
  top = Math.max(padding, Math.min(top, viewport.height - tooltipHeight - padding));

  return { top, left, actualPlacement };
}

// ============================================================================
// Component
// ============================================================================

export const SpotlightTour: React.FC<SpotlightTourProps> = ({
  active,
  steps,
  currentStep: controlledStep,
  onStepChange,
  onComplete,
  onClose,
  showProgress = true,
  showStepCounter = true,
  allowSkip = true,
  skipText,
  nextText,
  prevText,
  finishText,
  enableKeyboardNav = true,
  scrollToElement = true,
  scrollBehavior = 'smooth',
  storageKey,
  spotlightBorderRadius = 8,
  onNavigate,
}) => {
  const theme = useTheme();
  const { t } = useI18n();
  const isDark = theme.palette.mode === 'dark';
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Use i18n for default values
  const displaySkipText = skipText ?? t('tour.skip');
  const displayNextText = nextText ?? t('tour.next');
  const displayPrevText = prevText ?? t('tour.back');
  const displayFinishText = finishText ?? t('tour.finish');

  const [internalStep, setInternalStep] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  // Stable refs to avoid effect re-runs from parent state changes (e.g. trackEvent dispatches)
  const onNavigateRef = useRef(onNavigate);
  onNavigateRef.current = onNavigate;
  const scrollToElementRef = useRef(scrollToElement);
  scrollToElementRef.current = scrollToElement;
  const scrollBehaviorRef = useRef(scrollBehavior);
  scrollBehaviorRef.current = scrollBehavior;

  // Use controlled or internal step
  const currentStepIndex = controlledStep ?? internalStep;
  const currentStepData = steps[currentStepIndex];

  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;

  // Calculate progress
  const progress = useMemo(() => ((currentStepIndex + 1) / steps.length) * 100, [currentStepIndex, steps.length]);

  // Update step
  const setStep = useCallback(
    (step: number) => {
      // Call exit callback for current step
      currentStepData?.onExit?.();

      if (onStepChange) {
        onStepChange(step);
      } else {
        setInternalStep(step);
      }

      // Call enter callback for new step
      steps[step]?.onEnter?.();
    },
    [onStepChange, currentStepData, steps],
  );

  // Navigation handlers
  const handleNext = useCallback(() => {
    if (isLastStep) {
      onComplete?.();
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, 'completed');
        } catch {
          // Ignore localStorage errors
        }
      }
    } else {
      setStep(currentStepIndex + 1);
    }
  }, [isLastStep, currentStepIndex, setStep, onComplete, storageKey]);

  const handlePrev = useCallback(() => {
    if (!isFirstStep) {
      setStep(currentStepIndex - 1);
    }
  }, [isFirstStep, currentStepIndex, setStep]);

  const handleSkip = useCallback(() => {
    onClose?.();
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, 'skipped');
      } catch {
        // Ignore localStorage errors
      }
    }
  }, [onClose, storageKey]);

  // Stable ref for current step to avoid effect re-runs from parent re-renders.
  // The effect should only re-run when the step INDEX changes or active toggles,
  // not when the steps array reference changes due to unrelated state updates
  // (e.g. trackEvent dispatching during navigation).
  const stepTargetRef = useRef(currentStepData?.target);
  const stepPlacementRef = useRef(currentStepData?.placement);
  const stepRouteRef = useRef(currentStepData?.route);
  stepTargetRef.current = currentStepData?.target;
  stepPlacementRef.current = currentStepData?.placement;
  stepRouteRef.current = currentStepData?.route;

  // Update target element position (with navigation support for multi-page tours)
  useEffect(() => {
    if (!active || !stepTargetRef.current) return;

    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    const timers: ReturnType<typeof setTimeout>[] = [];
    let rafId: number | null = null;

    const updatePosition = () => {
      if (cancelled) return;
      const target = stepTargetRef.current;
      if (!target) return;
      const rect = getElementRect(target);
      setTargetRect(rect);

      if (rect && tooltipRef.current) {
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const pos = calculateTooltipPosition(
          rect,
          stepPlacementRef.current ?? 'auto',
          tooltipRect.width,
          tooltipRect.height,
        );
        setTooltipPosition({ top: pos.top, left: pos.left });
      }
    };

    const scheduledUpdate = (delay: number) => {
      timers.push(setTimeout(updatePosition, delay));
    };

    const scrollAndPosition = () => {
      const target = stepTargetRef.current;
      if (scrollToElementRef.current && target) {
        const element = document.querySelector(target);
        element?.scrollIntoView({ behavior: scrollBehaviorRef.current, block: 'start' });
        // Update position after scroll settles (smooth scroll can take 300-500ms)
        scheduledUpdate(350);
        scheduledUpdate(600);
      }
    };

    // Try to find the element; if not found and step has a route, navigate first
    const target = stepTargetRef.current;
    const route = stepRouteRef.current;
    const rect = target ? getElementRect(target) : null;
    if (!rect && route && onNavigateRef.current) {
      // Navigate to the step's route, then poll for the element to appear
      onNavigateRef.current(route);

      const MAX_POLL_ATTEMPTS = 40;
      const POLL_INTERVAL_MS = 200;
      let attempts = 0;

      const pollForElement = () => {
        if (cancelled) return;
        attempts++;
        const found = target ? getElementRect(target) : null;
        if (found) {
          updatePosition();
          scrollAndPosition();
        } else if (attempts < MAX_POLL_ATTEMPTS) {
          pollTimer = setTimeout(pollForElement, POLL_INTERVAL_MS);
        } else {
          // Element never appeared — show tooltip without highlight
          updatePosition();
        }
      };

      // Start polling after a short delay to allow route transition
      pollTimer = setTimeout(pollForElement, 300);
    } else {
      // Element found or no route to navigate to — position after a micro-delay
      // to ensure the tooltip ref is mounted
      rafId = requestAnimationFrame(() => {
        if (cancelled) return;
        updatePosition();
        scrollAndPosition();
        // Re-position after a short delay for layout shifts
        scheduledUpdate(100);
      });
    }

    // Update on resize/scroll
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
      for (const t of timers) clearTimeout(t);
      if (rafId != null) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
    // Only re-run when step index or active state changes, not on prop reference changes
  }, [active, currentStepIndex]);

  // Keyboard navigation
  useEffect(() => {
    if (!active || !enableKeyboardNav) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
        case 'Enter':
          e.preventDefault();
          handleNext();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          handlePrev();
          break;
        case 'Escape':
          e.preventDefault();
          handleSkip();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [active, enableKeyboardNav, handleNext, handlePrev, handleSkip]);

  if (!active || !currentStepData) return null;

  const highlightPadding = currentStepData.highlightPadding ?? 8;

  return (
    <Portal>
      <AnimatePresence>
        {/* Overlay container — creates a stacking context that ensures the
             box-shadow dimming renders above ALL page content (sidebar, header, etc.) */}
        <SpotlightOverlayContainer
          key="spotlight-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {targetRect && (
            <SpotlightCutout
              key="spotlight-cutout"
              style={{
                top: targetRect.top - highlightPadding,
                left: targetRect.left - highlightPadding,
                width: targetRect.width + highlightPadding * 2,
                height: targetRect.height + highlightPadding * 2,
                borderRadius: spotlightBorderRadius,
              }}
            />
          )}
          {/* Fallback when target not found — light backdrop without cutout */}
          {!targetRect && (
            <Box
              sx={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.18)',
              }}
            />
          )}
        </SpotlightOverlayContainer>

        {/* Tooltip */}
        <TooltipContainer
          key="spotlight-tooltip"
          ref={tooltipRef}
          $isDark={isDark}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.2 }}
          style={{
            top: tooltipPosition.top,
            left: tooltipPosition.left,
          }}
        >
          {/* Close button */}
          {allowSkip && (
            <IconButton
              size="small"
              onClick={handleSkip}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                opacity: 0.6,
                '&:hover': { opacity: 1 },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}

          {/* Progress bar */}
          {showProgress && (
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                mb: 2,
                borderRadius: 2,
                height: 4,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              }}
            />
          )}

          {/* Step counter */}
          {showStepCounter && (
            <Typography
              variant="overline"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
              }}
            >
              {t('tour.step', { current: currentStepIndex + 1, total: steps.length })}
            </Typography>
          )}

          {/* Title */}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mt: 0.5,
            }}
          >
            {currentStepData.title}
          </Typography>

          {/* Content */}
          <Typography
            variant="body2"
            component="div"
            sx={{
              color: 'text.secondary',
              mt: 1,
            }}
          >
            {currentStepData.content}
          </Typography>

          {/* Custom action */}
          {currentStepData.actionText && (
            <Button
              variant="outlined"
              size="small"
              onClick={currentStepData.onAction}
              sx={{ mt: 2 }}
              fullWidth
              color="primary"
            >
              {currentStepData.actionText}
            </Button>
          )}

          {/* Step indicators */}
          <StepIndicator>
            {steps.map((_, index) => (
              <StepDot key={index} $active={index === currentStepIndex} $completed={index < currentStepIndex} />
            ))}
          </StepIndicator>

          {/* Actions */}
          <ActionsContainer>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {!isFirstStep && (
                <Button size="small" onClick={handlePrev} startIcon={<PrevIcon />}>
                  {displayPrevText}
                </Button>
              )}
              {allowSkip && isFirstStep && (
                <Button size="small" onClick={handleSkip} color="inherit" sx={{ opacity: 0.7 }}>
                  {displaySkipText}
                </Button>
              )}
            </Box>

            <Button
              size="small"
              variant="contained"
              onClick={handleNext}
              endIcon={isLastStep ? <CompleteIcon /> : <NextIcon />}
              disabled={currentStepData.requireAction && !currentStepData.onAction}
            >
              {isLastStep ? displayFinishText : displayNextText}
            </Button>
          </ActionsContainer>

          {/* Keyboard hints */}
          {enableKeyboardNav && (
            <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${theme.palette.divider}`, display: 'flex', gap: 2 }}>
              <KeyboardHint keys={['←', '→']} label={t('keyboard.navigate')} />
              <KeyboardHint keys={['Esc']} label={t('keyboard.skip')} />
            </Box>
          )}
        </TooltipContainer>
      </AnimatePresence>
    </Portal>
  );
};

SpotlightTour.displayName = 'SpotlightTour';

export default SpotlightTour;
